import { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { LogOut, Home, TrendingUp, Calculator, Briefcase, Award, MessageSquare, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/o33cdnbv_profile_pic.png";

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

export default function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [partner, setPartner] = useState(null);
  const [notifStatus, setNotifStatus] = useState('default'); // default, granted, denied, unsupported

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC_KEY) {
        setNotifStatus('unsupported');
        return;
      }

      const permission = await Notification.requestPermission();
      setNotifStatus(permission);
      if (permission !== 'granted') return;

      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      const token = localStorage.getItem('rpace_token');
      await fetch(`${API_URL}/api/partners/push-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });
    } catch (err) {
      console.warn('Push subscription failed:', err);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('rpace_token');
    const data = localStorage.getItem('rpace_partner');
    if (!token || !data) {
      navigate('/r-pace-partner-login');
      return;
    }
    try {
      setPartner(JSON.parse(data));
    } catch {
      navigate('/r-pace-partner-login');
      return;
    }

    // Check current notification status
    if ('Notification' in window) {
      setNotifStatus(Notification.permission);
      if (Notification.permission === 'granted') {
        subscribeToPush();
      }
    } else {
      setNotifStatus('unsupported');
    }
  }, [navigate, subscribeToPush]);

  const handleLogout = () => {
    localStorage.removeItem('rpace_token');
    localStorage.removeItem('rpace_partner');
    navigate('/r-pace-partner-login');
  };

  if (!partner) return null;

  const tabs = [
    { to: '/r-pace/dashboard', label: 'Home', icon: Home },
    { to: '/r-pace/calculator', label: 'Calculator', icon: Calculator },
    { to: '/r-pace/pipeline', label: 'Pipeline', icon: TrendingUp },
    { to: '/r-pace/engagements', label: 'Live Cases', icon: Briefcase },
    { to: '/r-pace/credits', label: 'Credits', icon: Award },
    { to: '/r-pace/pitch-kit', label: 'Pitch', icon: MessageSquare },
  ];

  const isActive = (to) => {
    if (to === '/r-pace/dashboard') return location.pathname === to;
    if (to === '/r-pace/engagements') return location.pathname === to || location.pathname.startsWith('/r-pace/cases');
    return location.pathname === to;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0">
      {/* Desktop Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-12 md:h-14 flex items-center justify-between">
          <Link to="/r-pace/dashboard" className="flex items-center gap-2 md:gap-3">
            <img src={LOGO_URL} alt="Ratnatrraya" className="h-7 w-7 md:h-8 md:w-8" />
            <span className="font-semibold text-[#1e3a5f] text-xs md:text-sm">R-PACE</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-slate-700">{partner.name}</p>
              <p className="text-[10px] text-slate-400">{partner.profession} &bull; {partner.firm_name}</p>
            </div>
            <span className="text-[10px] md:text-xs bg-[#1e3a5f]/10 text-[#1e3a5f] px-2 py-0.5 md:py-1 rounded-full font-medium">{partner.profession}</span>
            {notifStatus !== 'unsupported' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={notifStatus === 'granted' ? undefined : subscribeToPush}
                className={`h-8 w-8 p-0 ${notifStatus === 'granted' ? 'text-green-500' : notifStatus === 'denied' ? 'text-red-400' : 'text-amber-500 animate-pulse'}`}
                title={notifStatus === 'granted' ? 'Notifications enabled' : notifStatus === 'denied' ? 'Notifications blocked' : 'Enable notifications'}
                data-testid="notif-bell-btn"
              >
                {notifStatus === 'granted' ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-red-600 h-8 w-8 p-0" data-testid="partner-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Tab Navigation — hidden on mobile */}
      <div className="bg-white border-b border-slate-200 hidden md:block">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {tabs.map(tab => (
            <Link key={tab.to} to={tab.to}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${isActive(tab.to) ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
              data-testid={`partner-tab-${tab.label.toLowerCase()}`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Content */}
      <main>
        <Outlet context={{ partner }} />
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-14 px-1">
          {tabs.map(tab => {
            const active = isActive(tab.to);
            return (
              <Link key={tab.to} to={tab.to}
                className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${active ? 'text-[#1e3a5f]' : 'text-slate-400'}`}
                data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
              >
                <tab.icon className={`w-5 h-5 ${active ? 'stroke-[2.5px]' : ''}`} />
                <span className={`text-[9px] mt-0.5 ${active ? 'font-bold' : 'font-medium'}`}>{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
